import React, { useCallback, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { SAMPLE_EMAIL } from "@/lib/sample";
import {
  FileText,
  Image as ImageIcon,
  Loader2,
  Mail,
  Paperclip,
  Sparkles,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";

const ACCEPT = ".pdf,.png,.jpg,.jpeg,.webp";
const MAX_FILES = 12;
const MAX_BYTES = 20 * 1024 * 1024;

const prettySize = (b) => {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

export const InputPanel = ({
  files,
  setFiles,
  emailBody,
  setEmailBody,
  onExtract,
  busy,
  onReset,
  onNotify,
}) => {
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const addFiles = useCallback(
    (incoming) => {
      const accepted = [];
      const rejected = [];
      Array.from(incoming || []).forEach((f) => {
        const okType = /\.(pdf|png|jpe?g|webp)$/i.test(f.name);
        if (!okType) {
          rejected.push(`${f.name} - unsupported type`);
          return;
        }
        if (f.size > MAX_BYTES) {
          rejected.push(`${f.name} - larger than 20 MB`);
          return;
        }
        accepted.push(f);
      });
      setFiles((prev) => {
        const merged = [...prev];
        accepted.forEach((f) => {
          if (!merged.some((m) => m.name === f.name && m.size === f.size)) merged.push(f);
        });
        return merged.slice(0, MAX_FILES);
      });
      if (rejected.length && onNotify) onNotify(rejected.join(" · "));
    },
    [setFiles, onNotify]
  );

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer?.files);
  };

  const canExtract = (files.length > 0 || emailBody.trim().length > 0) && !busy;

  return (
    <section className="grid grid-cols-1 gap-4 xl:grid-cols-2" data-testid="input-panel">
      {/* ---------------- upload ---------------- */}
      <div className="vde-card p-4 sm:p-5">
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Paperclip className="h-4 w-4 text-primary" aria-hidden="true" />
            <h2 className="text-sm font-semibold text-foreground">Attachments</h2>
          </div>
          <span className="text-[11px] text-muted-foreground">
            {files.length}/{MAX_FILES} files
          </span>
        </div>

        <div
          role="button"
          tabIndex={0}
          aria-label="Upload vendor documents"
          onClick={() => inputRef.current?.click()}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          data-testid="dropzone"
          className={cn(
            "flex cursor-pointer flex-col items-center justify-center rounded-xl border border-dashed px-4 py-8 text-center transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            dragging
              ? "border-ring bg-secondary"
              : "border-border bg-card hover:border-ring hover:bg-secondary/50"
          )}
        >
          <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-secondary">
            <UploadCloud className="h-5 w-5 text-primary" aria-hidden="true" />
          </div>
          <p className="text-sm font-medium text-foreground">
            Drop vendor files here, or click to browse
          </p>
          <p className="mt-1 text-xs text-muted-foreground">
            PDF, PNG, JPG, WEBP · up to 20 MB each · letterheads, bank mandates, cancelled cheques, GST certificates
          </p>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            className="hidden"
            data-testid="file-input"
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = "";
            }}
          />
        </div>

        {files.length > 0 ? (
          <ul className="mt-3 flex flex-wrap gap-2" data-testid="file-list">
            {files.map((f, i) => (
              <li
                key={`${f.name}-${f.size}-${i}`}
                className="inline-flex max-w-full items-center gap-2 rounded-full border border-border bg-secondary py-1 pl-2.5 pr-1.5 text-xs"
                data-testid="file-chip"
              >
                {/\.pdf$/i.test(f.name) ? (
                  <FileText className="h-3.5 w-3.5 shrink-0 text-danger-text" aria-hidden="true" />
                ) : (
                  <ImageIcon className="h-3.5 w-3.5 shrink-0 text-info-text" aria-hidden="true" />
                )}
                <span className="truncate" style={{ maxWidth: 190 }} title={f.name}>
                  {f.name}
                </span>
                <span className="vde-code shrink-0 text-[10px] text-muted-foreground">
                  {prettySize(f.size)}
                </span>
                <button
                  type="button"
                  aria-label={`Remove ${f.name}`}
                  data-testid="file-remove-button"
                  className="flex h-5 w-5 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-card hover:text-danger-text"
                  onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      {/* ---------------- email body ---------------- */}
      <div className="vde-card flex flex-col p-4 sm:p-5">
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-primary" aria-hidden="true" />
            <h2 className="text-sm font-semibold text-foreground">Email body</h2>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="text-[11px] font-medium text-primary underline-offset-2 transition-colors hover:underline"
              onClick={() => setEmailBody(SAMPLE_EMAIL)}
              data-testid="load-sample-button"
            >
              Load sample
            </button>
            {emailBody ? (
              <button
                type="button"
                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground transition-colors hover:text-danger-text"
                onClick={() => setEmailBody("")}
                data-testid="clear-email-button"
              >
                <Trash2 className="h-3 w-3" aria-hidden="true" /> Clear
              </button>
            ) : null}
          </div>
        </div>

        <Textarea
          value={emailBody}
          onChange={(e) => setEmailBody(e.target.value)}
          placeholder={"Paste the email body here - include payment notes, advance purpose, PO and invoice references. Reason/Comments is picked up from this text."}
          className="min-h-[200px] flex-1 resize-y bg-card text-[13px] leading-relaxed"
          data-testid="email-body-textarea"
        />
        <div className="mt-2 flex items-center justify-between text-[11px] text-muted-foreground">
          <span data-testid="email-char-count">{emailBody.trim().length} characters</span>
          <span>Files and email can be used together</span>
        </div>
      </div>

      {/* ---------------- CTA ---------------- */}
      <div className="xl:col-span-2">
        <div className="vde-card flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-5">
          <div className="flex items-center gap-3">
            <p className="text-xs text-muted-foreground">
              Every extraction is saved to History with the original email and files.
            </p>
            {files.length || emailBody ? (
              <button
                type="button"
                onClick={onReset}
                className="shrink-0 text-[11px] font-medium text-muted-foreground underline-offset-2 transition-colors hover:text-danger-text hover:underline"
                data-testid="clear-all-button"
              >
                Clear all
              </button>
            ) : null}
          </div>
          <Button
            size="lg"
            className="h-11 w-full gap-2 rounded-lg bg-primary text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 sm:w-auto"
            disabled={!canExtract}
            onClick={onExtract}
            data-testid="extract-vendor-details-button"
          >
            {busy ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <Sparkles className="h-4 w-4" aria-hidden="true" />
            )}
            {busy ? "Extracting…" : "Extract Vendor Details"}
          </Button>
        </div>
      </div>
    </section>
  );
};

export default InputPanel;
